/**
 * Operations Hub - Unified GPU Infrastructure Operations Control Plane
 * 
 * The SINGLE SOURCE OF TRUTH for all GPU container and instance operational movements.
 * Designed for SREs and platform engineers running production GPU fleets.
 * 
 * Information Architecture:
 * - Orders/Executions: High-level intent (Create Container, Terminate Instance, Scale Fleet)
 * - Steps/Actions: Atomic operations (API calls, state transitions)
 * - Events/Signals: System feedback and audit trail
 * 
 * Key Features:
 * - Real-time visibility into every operational movement
 * - Control plane vs data plane state tracking
 * - Correlation IDs linking related operations
 * - State mismatch detection and alerting
 * - Actionable error explanations
 * - Full audit trail for debugging and compliance
 * - Export for audit compliance (CSV, JSON)
 * - External observability links (CloudWatch, X-Ray)
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity,
  Search,
  RefreshCw,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Zap,
  Server,
  Network,
  RotateCcw,
  X,
  Calendar,
  User,
  Bot,
  Terminal,
  Shield,
  Cpu,
  Box,
  Layers,
  Play,
  Square,
  Trash2,
  ArrowUpDown,
  Workflow,
  Gauge,
  Eye,
  Link,
  Copy,
  MoreVertical,
  SlidersHorizontal,
  ListTree,
  LayoutGrid,
  Timer,
  TrendingUp,
  TrendingDown,
  Target,
  GitBranch,
  History,
  Download,
  FileJson,
  FileText,
  ExternalLink,
  Flame,
  ShieldAlert,
  Info,
  ChevronUp,
  Radio,
  Pause,
} from 'lucide-react';
import {
  operationsApi,
  Operation,
  OperationWithSteps,
  OperationFilters,
  OperationStatus,
  OperationType,
  TriggerSource,
  AttentionItems,
  OperationStats,
  OperationEvent,
  EventSeverity,
} from '../lib/api';
import OperationStepsTimeline from '../components/OperationStepsTimeline';
import { formatDistanceToNow, format, differenceInSeconds } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';

// ============================================
// Type Definitions
// ============================================

type ViewMode = 'table' | 'timeline' | 'grid';
type OperationCategory = 'all' | 'container' | 'instance' | 'network' | 'cluster';

interface FilterState {
  status: OperationStatus[];
  type: OperationType[];
  triggerSource: TriggerSource[];
  category: OperationCategory;
  timeRange: '1h' | '6h' | '24h' | '7d' | '30d' | 'custom';
  customStartDate?: string;
  customEndDate?: string;
  hasIssues: boolean;
  search: string;
}

// ============================================
// Constants
// ============================================

const OPERATION_CATEGORIES: Record<OperationCategory, OperationType[]> = {
  all: [],
  container: [
    'gpu_container_create',
    'gpu_container_start',
    'gpu_container_stop',
    'gpu_container_delete',
    'gpu_container_restart',
    'gpu_container_scale_up',
    'gpu_container_scale_down',
    'gpu_container_autoscale',
    'gpu_container_schedule',
    'gpu_container_reschedule',
    'gpu_container_migrate',
    'gpu_container_evict',
    'gpu_container_drain',
    'gpu_release',
    'container_recovery',
  ],
  instance: [
    'gpu_instance_create',
    'gpu_instance_terminate',
    'gpu_instance_start',
    'gpu_instance_stop',
    'gpu_instance_resize',
    'fleet_scale_up',
    'fleet_scale_down',
    'fleet_start',
    'fleet_stop',
    'fleet_terminate',
    'instance_repair',
  ],
  network: [
    'network_provision',
    'network_destroy',
    'network_modify_security',
    'vpc_attach',
    'vpc_detach',
  ],
  cluster: [
    'ecs_cluster_create',
    'ecs_cluster_delete',
    'ecs_task_run',
    'ecs_task_stop',
    'ecs_instance_terminate',
  ],
};

// Risk classification for operation types
const RISKY_OPERATIONS: OperationType[] = [
  'gpu_instance_terminate',
  'gpu_container_delete',
  'gpu_container_evict',
  'fleet_terminate',
  'network_destroy',
  'ecs_cluster_delete',
  'ecs_instance_terminate',
];

const CRITICAL_OPERATIONS: OperationType[] = [
  'fleet_terminate',
  'network_destroy',
  'ecs_cluster_delete',
];

const STATUS_CONFIG: Record<OperationStatus, {
  icon: React.ComponentType<any>;
  color: string;
  bgColor: string;
  borderColor: string;
  label: string;
  animate?: boolean;
}> = {
  queued: {
    icon: Clock,
    color: 'text-slate-400',
    bgColor: 'bg-slate-500/10',
    borderColor: 'border-slate-500/20',
    label: 'Queued',
  },
  running: {
    icon: Loader2,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/20',
    label: 'Running',
    animate: true,
  },
  blocked: {
    icon: AlertCircle,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/20',
    label: 'Blocked',
  },
  partial: {
    icon: AlertTriangle,
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/20',
    label: 'Partial',
  },
  completed: {
    icon: CheckCircle,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/20',
    label: 'Completed',
  },
  failed: {
    icon: XCircle,
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/20',
    label: 'Failed',
  },
  cancelled: {
    icon: X,
    color: 'text-slate-400',
    bgColor: 'bg-slate-500/10',
    borderColor: 'border-slate-500/20',
    label: 'Cancelled',
  },
  rolled_back: {
    icon: RotateCcw,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/20',
    label: 'Rolled Back',
  },
};

const TRIGGER_CONFIG: Record<TriggerSource, {
  icon: React.ComponentType<any>;
  color: string;
  label: string;
}> = {
  user: { icon: User, color: 'text-blue-400', label: 'User' },
  api: { icon: Terminal, color: 'text-purple-400', label: 'API' },
  autoscaler: { icon: Zap, color: 'text-green-400', label: 'Auto-scaler' },
  scheduler: { icon: Calendar, color: 'text-amber-400', label: 'Scheduler' },
  policy: { icon: Shield, color: 'text-orange-400', label: 'Policy' },
  spot_handler: { icon: AlertTriangle, color: 'text-yellow-400', label: 'Spot Handler' },
  health_check: { icon: Activity, color: 'text-pink-400', label: 'Health Check' },
  system: { icon: Bot, color: 'text-slate-400', label: 'System' },
};

const TYPE_CONFIG: Partial<Record<OperationType, {
  label: string;
  icon: React.ComponentType<any>;
  category: OperationCategory;
  severity: 'info' | 'warning' | 'critical';
}>> = {
  // Container lifecycle
  gpu_container_create: { label: 'Create Container', icon: Box, category: 'container', severity: 'info' },
  gpu_container_start: { label: 'Start Container', icon: Play, category: 'container', severity: 'info' },
  gpu_container_stop: { label: 'Stop Container', icon: Square, category: 'container', severity: 'warning' },
  gpu_container_delete: { label: 'Delete Container', icon: Trash2, category: 'container', severity: 'critical' },
  gpu_container_restart: { label: 'Restart Container', icon: RefreshCw, category: 'container', severity: 'warning' },
  // Container scaling
  gpu_container_scale_up: { label: 'Scale Up', icon: TrendingUp, category: 'container', severity: 'info' },
  gpu_container_scale_down: { label: 'Scale Down', icon: TrendingDown, category: 'container', severity: 'warning' },
  gpu_container_autoscale: { label: 'Auto-scale', icon: Zap, category: 'container', severity: 'info' },
  // Container scheduling
  gpu_container_schedule: { label: 'Schedule', icon: Calendar, category: 'container', severity: 'info' },
  gpu_container_reschedule: { label: 'Reschedule', icon: ArrowUpDown, category: 'container', severity: 'warning' },
  gpu_container_migrate: { label: 'Migrate', icon: Workflow, category: 'container', severity: 'warning' },
  // Container cleanup
  gpu_container_evict: { label: 'Evict', icon: AlertTriangle, category: 'container', severity: 'critical' },
  gpu_container_drain: { label: 'Drain', icon: Gauge, category: 'container', severity: 'warning' },
  gpu_release: { label: 'Release GPU', icon: Cpu, category: 'container', severity: 'info' },
  container_recovery: { label: 'Container Recovery', icon: RotateCcw, category: 'container', severity: 'critical' },
  // Instance lifecycle
  gpu_instance_create: { label: 'Create Instance', icon: Server, category: 'instance', severity: 'info' },
  gpu_instance_terminate: { label: 'Terminate Instance', icon: Trash2, category: 'instance', severity: 'critical' },
  gpu_instance_start: { label: 'Start Instance', icon: Play, category: 'instance', severity: 'info' },
  gpu_instance_stop: { label: 'Stop Instance', icon: Square, category: 'instance', severity: 'warning' },
  // Fleet operations
  fleet_scale_up: { label: 'Scale Up Fleet', icon: TrendingUp, category: 'instance', severity: 'info' },
  fleet_scale_down: { label: 'Scale Down Fleet', icon: TrendingDown, category: 'instance', severity: 'warning' },
  fleet_terminate: { label: 'Terminate Fleet', icon: Trash2, category: 'instance', severity: 'critical' },
  // Network operations
  network_provision: { label: 'Provision Network', icon: Network, category: 'network', severity: 'info' },
  network_destroy: { label: 'Destroy Network', icon: Trash2, category: 'network', severity: 'critical' },
  // Cluster operations
  ecs_cluster_create: { label: 'Create Cluster', icon: Layers, category: 'cluster', severity: 'info' },
  ecs_cluster_delete: { label: 'Delete Cluster', icon: Trash2, category: 'cluster', severity: 'critical' },
  ecs_task_run: { label: 'Run Task', icon: Play, category: 'cluster', severity: 'info' },
  ecs_task_stop: { label: 'Stop Task', icon: Square, category: 'cluster', severity: 'warning' },
  ecs_instance_terminate: { label: 'Terminate ECS Instance', icon: Trash2, category: 'cluster', severity: 'critical' },
};

const EVENT_SEVERITY_CONFIG: Record<EventSeverity, {
  icon: React.ComponentType<any>;
  color: string;
  bgColor: string;
  label: string;
}> = {
  debug: { icon: Terminal, color: 'text-slate-400', bgColor: 'bg-slate-500/10', label: 'Debug' },
  info: { icon: Info, color: 'text-blue-400', bgColor: 'bg-blue-500/10', label: 'Info' },
  warning: { icon: AlertTriangle, color: 'text-amber-400', bgColor: 'bg-amber-500/10', label: 'Warning' },
  error: { icon: XCircle, color: 'text-red-400', bgColor: 'bg-red-500/10', label: 'Error' },
  critical: { icon: Flame, color: 'text-red-500', bgColor: 'bg-red-500/20', label: 'Critical' },
};

// ============================================
// Utility Functions
// ============================================

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function formatLiveDuration(startDate: string): string {
  const seconds = differenceInSeconds(new Date(), new Date(startDate));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSecs = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSecs}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;
  return `${hours}h ${remainingMins}m`;
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).then(() => {
    toast.success('Copied to clipboard');
  }).catch(() => {
    toast.error('Failed to copy');
  });
}

function isRiskyOperation(type: OperationType): boolean {
  return RISKY_OPERATIONS.includes(type);
}

function isCriticalOperation(type: OperationType): boolean {
  return CRITICAL_OPERATIONS.includes(type);
}

function getAWSConsoleLink(resourceType: string, resourceId: string, region = 'us-east-1'): string {
  const baseUrl = `https://${region}.console.aws.amazon.com`;
  
  switch (resourceType) {
    case 'instance':
      return `${baseUrl}/ec2/v2/home?region=${region}#InstanceDetails:instanceId=${resourceId}`;
    case 'vpc':
      return `${baseUrl}/vpc/home?region=${region}#VpcDetails:VpcId=${resourceId}`;
    case 'subnet':
      return `${baseUrl}/vpc/home?region=${region}#SubnetDetails:subnetId=${resourceId}`;
    case 'security_group':
      return `${baseUrl}/ec2/v2/home?region=${region}#SecurityGroup:groupId=${resourceId}`;
    case 'ecs_cluster':
      return `${baseUrl}/ecs/home?region=${region}#/clusters/${resourceId}`;
    case 'task':
      return `${baseUrl}/ecs/home?region=${region}#/clusters/default/tasks/${resourceId}`;
    default:
      return `${baseUrl}/console/home?region=${region}`;
  }
}

function getCloudWatchLogsLink(logGroup: string, region = 'us-east-1'): string {
  return `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}#logsV2:log-groups/log-group/${encodeURIComponent(logGroup)}`;
}

// Export operations to CSV
function exportToCSV(operations: Operation[]): void {
  const headers = [
    'ID',
    'Correlation ID',
    'Type',
    'Status',
    'Title',
    'Trigger Source',
    'Instance ID',
    'Started At',
    'Completed At',
    'Duration (ms)',
    'Progress',
    'Error Message',
  ].join(',');

  const rows = operations.map(op => [
    op.id,
    op.correlationId,
    op.type,
    op.status,
    `"${op.title.replace(/"/g, '""')}"`,
    op.triggerSource,
    op.targetResources?.instanceId || '',
    op.startedAt || '',
    op.completedAt || '',
    op.durationMs || '',
    op.progress,
    `"${(op.errorMessage || '').replace(/"/g, '""')}"`,
  ].join(','));

  const csv = [headers, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `operations-export-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success('Exported to CSV');
}

// Export operations to JSON
function exportToJSON(operations: Operation[]): void {
  const json = JSON.stringify(operations, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `operations-export-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.json`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success('Exported to JSON');
}

// ============================================
// Sub-Components
// ============================================

// Status Badge
function StatusBadge({ status, size = 'sm' }: { status: OperationStatus; size?: 'xs' | 'sm' | 'md' }) {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  const sizeClasses = {
    xs: 'px-1.5 py-0.5 text-[10px] gap-1',
    sm: 'px-2 py-0.5 text-xs gap-1',
    md: 'px-2.5 py-1 text-sm gap-1.5',
  };
  const iconSizes = { xs: 'w-2.5 h-2.5', sm: 'w-3 h-3', md: 'w-4 h-4' };

  return (
    <span className={clsx(
      'inline-flex items-center rounded-full font-medium border',
      config.bgColor, config.borderColor, config.color, sizeClasses[size]
    )}>
      <Icon className={clsx(iconSizes[size], config.animate && 'animate-spin')} />
      {config.label}
    </span>
  );
}

// Trigger Badge
function TriggerBadge({ source, details }: { source: TriggerSource; details?: string }) {
  const config = TRIGGER_CONFIG[source] || TRIGGER_CONFIG.system;
  const Icon = config.icon;

  return (
    <span className={clsx('inline-flex items-center gap-1 text-xs', config.color)} title={details}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

// Operation Type Badge with risk indicator
function TypeBadge({ type }: { type: OperationType }) {
  const config = TYPE_CONFIG[type];
  const label = config?.label || type.replace(/_/g, ' ');
  const Icon = config?.icon || Activity;
  const isRisky = isRiskyOperation(type);
  const isCritical = isCriticalOperation(type);
  
  const severityColors = {
    info: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    warning: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    critical: 'bg-red-500/10 text-red-400 border-red-500/20',
  };
  const colorClass = config?.severity ? severityColors[config.severity] : 'bg-surface-800 text-surface-300 border-surface-700';

  return (
    <span className={clsx(
      'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border',
      colorClass,
      isCritical && 'ring-1 ring-red-500/50'
    )}>
      <Icon className="w-3 h-3" />
      {label}
      {isRisky && (
        <span title="Risky Operation">
          <ShieldAlert className="w-3 h-3 text-orange-400 ml-0.5" />
        </span>
      )}
    </span>
  );
}

// Category Badge
function CategoryBadge({ category }: { category: OperationCategory }) {
  const colors: Record<OperationCategory, string> = {
    all: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
    container: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    instance: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
    network: 'bg-green-500/10 text-green-400 border-green-500/20',
    cluster: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  };

  return (
    <span className={clsx(
      'text-[10px] px-1.5 py-0.5 rounded border uppercase font-medium tracking-wide',
      colors[category]
    )}>
      {category}
    </span>
  );
}

// ID with copy button
function IdWithCopy({ id, label, truncate = 12 }: { id: string; label?: string; truncate?: number }) {
  return (
    <span className="inline-flex items-center gap-1 group">
      {label && <span className="text-surface-500 text-xs">{label}:</span>}
      <code 
        className="text-xs font-mono text-surface-400 cursor-pointer hover:text-surface-200 transition"
        onClick={() => copyToClipboard(id)}
        title={id}
      >
        {id.slice(0, truncate)}...
      </code>
      <Copy className="w-3 h-3 text-surface-600 opacity-0 group-hover:opacity-100 transition cursor-pointer" 
        onClick={() => copyToClipboard(id)} 
      />
    </span>
  );
}

// Live Duration Counter
function LiveDuration({ startDate }: { startDate: string }) {
  const [, setTick] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className="text-xs text-blue-400 font-mono tabular-nums">
      {formatLiveDuration(startDate)}
    </span>
  );
}

// Risk Indicator for operations
function RiskIndicator({ type }: { type: OperationType }) {
  const isCritical = isCriticalOperation(type);
  const isRisky = isRiskyOperation(type);
  
  if (isCritical) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium 
                       bg-red-500/20 text-red-400 border border-red-500/30 rounded-full animate-pulse">
        <Flame className="w-2.5 h-2.5" />
        CRITICAL
      </span>
    );
  }
  
  if (isRisky) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium 
                       bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded-full">
        <ShieldAlert className="w-2.5 h-2.5" />
        RISKY
      </span>
    );
  }
  
  return null;
}

// External Links Panel
function ExternalLinksPanel({ operation }: { operation: Operation }) {
  const resources = operation.targetResources;
  const region = resources?.region || 'us-east-1';
  
  const links: Array<{ label: string; url: string; icon: React.ComponentType<any> }> = [];
  
  if (resources?.instanceId) {
    links.push({
      label: 'EC2 Console',
      url: getAWSConsoleLink('instance', resources.instanceId, region),
      icon: Server,
    });
  }
  
  if (resources?.vpcId) {
    links.push({
      label: 'VPC Console',
      url: getAWSConsoleLink('vpc', resources.vpcId, region),
      icon: Network,
    });
  }
  
  if (resources?.clusterName) {
    links.push({
      label: 'ECS Console',
      url: getAWSConsoleLink('ecs_cluster', resources.clusterName, region),
      icon: Layers,
    });
  }
  
  // Add CloudWatch link
  links.push({
    label: 'CloudWatch Logs',
    url: getCloudWatchLogsLink('/gpu-platform/operations', region),
    icon: Terminal,
  });
  
  if (links.length === 0) return null;
  
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs text-surface-500">AWS:</span>
      {links.map((link, i) => {
        const Icon = link.icon;
        return (
          <a
            key={i}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 
                       px-2 py-0.5 rounded bg-blue-500/10 border border-blue-500/20 
                       hover:bg-blue-500/20 transition"
          >
            <Icon className="w-3 h-3" />
            {link.label}
            <ExternalLink className="w-2.5 h-2.5" />
          </a>
        );
      })}
    </div>
  );
}

// ============================================
// Export Dialog Component
// ============================================

function ExportDialog({ 
  operations, 
  isOpen, 
  onClose 
}: { 
  operations: Operation[]; 
  isOpen: boolean; 
  onClose: () => void;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-md p-6 bg-surface-900 border border-surface-700 rounded-xl shadow-2xl"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Download className="w-5 h-5 text-blue-400" />
            Export Operations
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-surface-800 rounded-lg transition">
            <X className="w-5 h-5 text-surface-400" />
          </button>
        </div>
        
        <p className="text-sm text-surface-400 mb-4">
          Export {operations.length} operation{operations.length !== 1 ? 's' : ''} for audit and compliance purposes.
        </p>
        
        <div className="space-y-3">
          <button
            onClick={() => {
              exportToCSV(operations);
              onClose();
            }}
            className="w-full flex items-center gap-3 p-4 bg-surface-800 border border-surface-700 
                       rounded-lg hover:border-blue-500/50 hover:bg-surface-800/80 transition group"
          >
            <div className="p-2 bg-emerald-500/20 rounded-lg group-hover:bg-emerald-500/30 transition">
              <FileText className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="flex-1 text-left">
              <div className="font-medium text-white">Export as CSV</div>
              <div className="text-xs text-surface-400">
                Spreadsheet-compatible format for Excel, Google Sheets
              </div>
            </div>
          </button>
          
          <button
            onClick={() => {
              exportToJSON(operations);
              onClose();
            }}
            className="w-full flex items-center gap-3 p-4 bg-surface-800 border border-surface-700 
                       rounded-lg hover:border-blue-500/50 hover:bg-surface-800/80 transition group"
          >
            <div className="p-2 bg-amber-500/20 rounded-lg group-hover:bg-amber-500/30 transition">
              <FileJson className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1 text-left">
              <div className="font-medium text-white">Export as JSON</div>
              <div className="text-xs text-surface-400">
                Full data export for programmatic processing
              </div>
            </div>
          </button>
        </div>
        
        <div className="mt-4 p-3 bg-surface-800/50 rounded-lg">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-surface-400">
              Exports include all operation metadata, timestamps, and error details. 
              Audit logs are retained for 30 days.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ============================================
// Operational Intelligence Panel
// ============================================

interface IntelligencePanelProps {
  attention: AttentionItems | null;
  stats: OperationStats | null;
  onFilterIssues: () => void;
}

function OperationalIntelligencePanel({ attention, stats, onFilterIssues }: IntelligencePanelProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const hasIssues = attention && attention.totalCount > 0;
  
  return (
    <div className="mb-6 space-y-4">
      {/* Attention Banner */}
      {hasIssues && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-gradient-to-r from-red-500/10 via-amber-500/10 to-orange-500/10 
                     border border-amber-500/30 rounded-xl"
        >
          <div className="flex items-start gap-4">
            <div className="p-2 bg-amber-500/20 rounded-lg mt-0.5">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-amber-200 flex items-center gap-2">
                  <Radio className="w-4 h-4 animate-pulse" />
                  Operator Attention Required ({attention.totalCount})
                </h3>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={onFilterIssues}
                    className="text-xs text-amber-400 hover:text-amber-300 font-medium px-3 py-1.5 
                               rounded-lg hover:bg-amber-500/10 transition flex items-center gap-1"
                  >
                    <Eye className="w-3 h-3" />
                    Review All
                  </button>
                  <button
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className="p-1 hover:bg-amber-500/10 rounded-lg transition"
                  >
                    {isCollapsed ? (
                      <ChevronDown className="w-4 h-4 text-amber-400" />
                    ) : (
                      <ChevronUp className="w-4 h-4 text-amber-400" />
                    )}
                  </button>
                </div>
              </div>
              
              <AnimatePresence>
                {!isCollapsed && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="grid grid-cols-4 gap-3">
                      {attention.blockedOperations.length > 0 && (
                        <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg 
                                        hover:border-amber-500/40 transition cursor-pointer"
                             onClick={onFilterIssues}>
                          <div className="flex items-center gap-2 mb-1">
                            <Pause className="w-4 h-4 text-amber-400" />
                            <span className="text-lg font-bold text-amber-400">
                              {attention.blockedOperations.length}
                            </span>
                          </div>
                          <span className="text-xs text-amber-300/70">Blocked Operations</span>
                          <p className="text-[10px] text-amber-400/50 mt-1">
                            Waiting on dependencies or resources
                          </p>
                        </div>
                      )}
                      
                      {attention.stateMismatches.length > 0 && (
                        <div className="p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg
                                        hover:border-orange-500/40 transition cursor-pointer"
                             onClick={onFilterIssues}>
                          <div className="flex items-center gap-2 mb-1">
                            <AlertCircle className="w-4 h-4 text-orange-400" />
                            <span className="text-lg font-bold text-orange-400">
                              {attention.stateMismatches.length}
                            </span>
                          </div>
                          <span className="text-xs text-orange-300/70">State Mismatches</span>
                          <p className="text-[10px] text-orange-400/50 mt-1">
                            Control plane ≠ data plane
                          </p>
                        </div>
                      )}
                      
                      {attention.longRunning.length > 0 && (
                        <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg
                                        hover:border-blue-500/40 transition cursor-pointer"
                             onClick={onFilterIssues}>
                          <div className="flex items-center gap-2 mb-1">
                            <Timer className="w-4 h-4 text-blue-400" />
                            <span className="text-lg font-bold text-blue-400">
                              {attention.longRunning.length}
                            </span>
                          </div>
                          <span className="text-xs text-blue-300/70">Long-Running (&gt;10min)</span>
                          <p className="text-[10px] text-blue-400/50 mt-1">
                            Operations exceeding expected duration
                          </p>
                        </div>
                      )}
                      
                      {attention.orphanedResources.length > 0 && (
                        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg
                                        hover:border-red-500/40 transition cursor-pointer"
                             onClick={onFilterIssues}>
                          <div className="flex items-center gap-2 mb-1">
                            <Trash2 className="w-4 h-4 text-red-400" />
                            <span className="text-lg font-bold text-red-400">
                              {attention.orphanedResources.length}
                            </span>
                          </div>
                          <span className="text-xs text-red-300/70">Orphaned Resources</span>
                          <p className="text-[10px] text-red-400/50 mt-1">
                            Resources without parent operation
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-6 gap-3">
          <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-xl hover:border-blue-500/40 transition">
            <div className="flex items-center gap-2 mb-1">
              <Loader2 className={clsx('w-4 h-4 text-blue-400', 
                ((stats.byStatus.running || 0) + (stats.byStatus.queued || 0)) > 0 && 'animate-spin'
              )} />
              <span className="text-xs text-surface-400">Active</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {(stats.byStatus.running || 0) + (stats.byStatus.queued || 0)}
            </div>
          </div>
          
          <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-xl hover:border-emerald-500/40 transition">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-surface-400">Completed</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.byStatus.completed || 0}</div>
          </div>
          
          <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-xl hover:border-red-500/40 transition">
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="w-4 h-4 text-red-400" />
              <span className="text-xs text-surface-400">Failed</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.byStatus.failed || 0}</div>
          </div>
          
          <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl hover:border-amber-500/40 transition">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-surface-400">Blocked</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.byStatus.blocked || 0}</div>
          </div>
          
          <div className="p-4 bg-surface-800/50 border border-surface-700 rounded-xl hover:border-surface-600 transition">
            <div className="flex items-center gap-2 mb-1">
              <Target className="w-4 h-4 text-surface-400" />
              <span className="text-xs text-surface-400">Success Rate</span>
            </div>
            <div className={clsx(
              'text-2xl font-bold',
              stats.successRate >= 95 ? 'text-emerald-400' :
              stats.successRate >= 80 ? 'text-amber-400' : 'text-red-400'
            )}>
              {stats.successRate.toFixed(1)}%
            </div>
          </div>
          
          <div className="p-4 bg-surface-800/50 border border-surface-700 rounded-xl hover:border-surface-600 transition">
            <div className="flex items-center gap-2 mb-1">
              <Timer className="w-4 h-4 text-surface-400" />
              <span className="text-xs text-surface-400">Avg Duration</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {stats.avgDurationMs > 0 ? formatDuration(stats.avgDurationMs) : '—'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// Filter Bar Component
// ============================================

interface FilterBarProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onExport: () => void;
  operationsCount: number;
}

function FilterBar({ filters, onFiltersChange, viewMode, onViewModeChange, onExport, operationsCount }: FilterBarProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const statusOptions: OperationStatus[] = ['queued', 'running', 'blocked', 'partial', 'completed', 'failed', 'cancelled'];
  const triggerOptions: TriggerSource[] = ['user', 'api', 'autoscaler', 'scheduler', 'policy', 'spot_handler', 'health_check', 'system'];
  const categoryOptions: OperationCategory[] = ['all', 'container', 'instance', 'network', 'cluster'];
  const timeRangeOptions = [
    { value: '1h', label: 'Last Hour' },
    { value: '6h', label: 'Last 6 Hours' },
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
  ];

  const hasActiveFilters = filters.status.length > 0 || 
    filters.type.length > 0 || 
    filters.triggerSource.length > 0 ||
    filters.category !== 'all' ||
    filters.hasIssues;

  const clearFilters = () => {
    onFiltersChange({
      status: [],
      type: [],
      triggerSource: [],
      category: 'all',
      timeRange: '24h',
      hasIssues: false,
      search: '',
    });
  };

  return (
    <div className="mb-6 space-y-3">
      {/* Primary Filter Row */}
      <div className="flex items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
          <input
            type="text"
            placeholder="Search by ID, correlation ID, resource ID, or title..."
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-surface-800 border border-surface-700 
                       rounded-lg text-sm focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500
                       placeholder:text-surface-500"
          />
        </div>

        {/* Category Quick Filter */}
        <div className="flex items-center gap-1 p-1 bg-surface-800 rounded-lg border border-surface-700">
          {categoryOptions.map((cat) => (
            <button
              key={cat}
              onClick={() => updateFilter('category', cat)}
              className={clsx(
                'px-3 py-1.5 text-xs font-medium rounded-md transition capitalize',
                filters.category === cat
                  ? 'bg-blue-500 text-white'
                  : 'text-surface-400 hover:text-surface-200 hover:bg-surface-700'
              )}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Time Range */}
        <select
          value={filters.timeRange}
          onChange={(e) => updateFilter('timeRange', e.target.value as FilterState['timeRange'])}
          className="px-3 py-2.5 bg-surface-800 border border-surface-700 rounded-lg text-sm 
                     focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 text-surface-300"
        >
          {timeRangeOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        {/* View Mode */}
        <div className="flex items-center gap-1 p-1 bg-surface-800 rounded-lg border border-surface-700">
          <button
            onClick={() => onViewModeChange('table')}
            className={clsx(
              'p-1.5 rounded transition',
              viewMode === 'table' ? 'bg-blue-500 text-white' : 'text-surface-400 hover:text-surface-200'
            )}
            title="Table View"
          >
            <ListTree className="w-4 h-4" />
          </button>
          <button
            onClick={() => onViewModeChange('timeline')}
            className={clsx(
              'p-1.5 rounded transition',
              viewMode === 'timeline' ? 'bg-blue-500 text-white' : 'text-surface-400 hover:text-surface-200'
            )}
            title="Timeline View"
          >
            <GitBranch className="w-4 h-4" />
          </button>
          <button
            onClick={() => onViewModeChange('grid')}
            className={clsx(
              'p-1.5 rounded transition',
              viewMode === 'grid' ? 'bg-blue-500 text-white' : 'text-surface-400 hover:text-surface-200'
            )}
            title="Grid View"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
        </div>

        {/* Advanced Filters Toggle */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={clsx(
            'flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition',
            showAdvanced 
              ? 'bg-blue-500/10 border-blue-500/30 text-blue-400'
              : 'bg-surface-800 border-surface-700 text-surface-400 hover:border-surface-600'
          )}
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filters
          {hasActiveFilters && (
            <span className="w-2 h-2 rounded-full bg-blue-500" />
          )}
        </button>

        {/* Issues Toggle */}
        <button
          onClick={() => updateFilter('hasIssues', !filters.hasIssues)}
          className={clsx(
            'flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition',
            filters.hasIssues 
              ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
              : 'bg-surface-800 border-surface-700 text-surface-400 hover:border-surface-600'
          )}
        >
          <AlertTriangle className="w-4 h-4" />
          Issues Only
        </button>

        {/* Export Button */}
        <button
          onClick={onExport}
          disabled={operationsCount === 0}
          className="flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition
                     bg-surface-800 border-surface-700 text-surface-400 hover:border-surface-600
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-4 h-4" />
          Export
        </button>
      </div>

      {/* Advanced Filters */}
      <AnimatePresence>
        {showAdvanced && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 bg-surface-800/50 border border-surface-700 rounded-lg space-y-4">
              {/* Status Filter */}
              <div>
                <label className="text-xs font-medium text-surface-400 mb-2 block">Status</label>
                <div className="flex flex-wrap gap-2">
                  {statusOptions.map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        const newStatuses = filters.status.includes(status)
                          ? filters.status.filter(s => s !== status)
                          : [...filters.status, status];
                        updateFilter('status', newStatuses);
                      }}
                      className={clsx(
                        'px-2.5 py-1 text-xs rounded-lg border transition',
                        filters.status.includes(status)
                          ? `${STATUS_CONFIG[status].bgColor} ${STATUS_CONFIG[status].borderColor} ${STATUS_CONFIG[status].color}`
                          : 'bg-surface-800 border-surface-700 text-surface-400 hover:border-surface-600'
                      )}
                    >
                      {STATUS_CONFIG[status].label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Trigger Source Filter */}
              <div>
                <label className="text-xs font-medium text-surface-400 mb-2 block">Trigger Source</label>
                <div className="flex flex-wrap gap-2">
                  {triggerOptions.map((trigger) => (
                    <button
                      key={trigger}
                      onClick={() => {
                        const newTriggers = filters.triggerSource.includes(trigger)
                          ? filters.triggerSource.filter(t => t !== trigger)
                          : [...filters.triggerSource, trigger];
                        updateFilter('triggerSource', newTriggers);
                      }}
                      className={clsx(
                        'px-2.5 py-1 text-xs rounded-lg border transition flex items-center gap-1',
                        filters.triggerSource.includes(trigger)
                          ? 'bg-blue-500/10 border-blue-500/30 text-blue-400'
                          : 'bg-surface-800 border-surface-700 text-surface-400 hover:border-surface-600'
                      )}
                    >
                      {TRIGGER_CONFIG[trigger].label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <div className="flex justify-end">
                  <button
                    onClick={clearFilters}
                    className="text-xs text-surface-400 hover:text-surface-300 px-3 py-1.5 
                               rounded-lg hover:bg-surface-700 transition"
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// Operation Row Component
// ============================================

interface OperationRowProps {
  operation: Operation;
  isExpanded: boolean;
  onToggle: () => void;
  expandedDetails?: OperationWithSteps | null;
  isLoadingDetails?: boolean;
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
  onViewCorrelated: (correlationId: string) => void;
}

function OperationRow({
  operation,
  isExpanded,
  onToggle,
  expandedDetails,
  isLoadingDetails,
  onCancel,
  onRetry,
  onViewCorrelated,
}: OperationRowProps) {
  const typeConfig = TYPE_CONFIG[operation.type];
  const isActive = ['running', 'queued'].includes(operation.status);
  const hasIssues = operation.hasStateMismatch || 
    operation.hasOrphanedResources || 
    (operation.blockingFactors && operation.blockingFactors.length > 0);
  const isCritical = isCriticalOperation(operation.type);
  const isRisky = isRiskyOperation(operation.type);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.98 }}
      className={clsx(
        'rounded-xl border overflow-hidden transition-all group',
        isExpanded 
          ? 'bg-surface-800/80 border-surface-600 shadow-lg' 
          : 'bg-surface-900/50 border-surface-800 hover:border-surface-700',
        hasIssues && !isExpanded && 'border-l-2 border-l-amber-500',
        isActive && !isExpanded && 'border-l-2 border-l-blue-500',
        isCritical && !isExpanded && 'border-l-2 border-l-red-500',
        isRisky && !isCritical && !isExpanded && 'border-l-2 border-l-orange-500'
      )}
    >
      {/* Collapsed Row */}
      <div 
        onClick={onToggle}
        className="w-full p-4 flex items-center gap-4 text-left cursor-pointer"
      >
        {/* Expand Icon */}
        <div className="text-surface-500">
          {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </div>

        {/* Operation ID */}
        <div className="w-24 flex-shrink-0">
          <IdWithCopy id={operation.id} truncate={8} />
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-medium text-white truncate max-w-[300px]">{operation.title}</span>
            <StatusBadge status={operation.status} />
            <TypeBadge type={operation.type} />
            {typeConfig && <CategoryBadge category={typeConfig.category} />}
            <RiskIndicator type={operation.type} />
          </div>
          <div className="flex items-center gap-3 text-xs text-surface-400">
            <TriggerBadge source={operation.triggerSource} details={operation.triggerDetails} />
            
            {/* Target Resources */}
            {operation.targetResources?.instanceId && (
              <span className="flex items-center gap-1" title={operation.targetResources.instanceId}>
                <Server className="w-3 h-3" />
                {operation.targetResources.instanceId.slice(0, 10)}...
              </span>
            )}
            {operation.targetResources?.containerId && (
              <span className="flex items-center gap-1" title={operation.targetResources.containerId}>
                <Box className="w-3 h-3" />
                {operation.targetResources.containerId.slice(0, 10)}...
              </span>
            )}
            {operation.targetResources?.podName && (
              <span className="flex items-center gap-1">
                <Layers className="w-3 h-3" />
                {operation.targetResources.podName}
              </span>
            )}
            {operation.targetResources?.gpuCount && (
              <span className="flex items-center gap-1">
                <Cpu className="w-3 h-3" />
                {operation.targetResources.gpuCount} GPU{operation.targetResources.gpuCount > 1 ? 's' : ''}
              </span>
            )}
            
            {/* Issue Indicators */}
            {hasIssues && (
              <span className="flex items-center gap-1 text-amber-400">
                <AlertTriangle className="w-3 h-3" />
                Issues
              </span>
            )}
          </div>
        </div>

        {/* Progress */}
        {isActive && (
          <div className="w-36">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-surface-400">{operation.progress}%</span>
              <span className="text-surface-500">
                {operation.completedActions}/{operation.totalActions} steps
              </span>
            </div>
            <div className="h-1.5 bg-surface-700 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-blue-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${operation.progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Duration */}
        <div className="w-24 text-right">
          {isActive && operation.startedAt ? (
            <LiveDuration startDate={operation.startedAt} />
          ) : operation.durationMs ? (
            <span className="text-xs text-surface-400">{formatDuration(operation.durationMs)}</span>
          ) : (
            <span className="text-xs text-surface-500">
              {formatDistanceToNow(new Date(operation.createdAt), { addSuffix: true })}
            </span>
          )}
        </div>

        {/* Quick Actions */}
        <div className="w-8 opacity-0 group-hover:opacity-100 transition" onClick={(e) => e.stopPropagation()}>
          <div className="relative">
            <button className="p-1.5 hover:bg-surface-700 rounded transition">
              <MoreVertical className="w-4 h-4 text-surface-500" />
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Detail */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-surface-700"
          >
            {isLoadingDetails ? (
              <div className="p-8 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-surface-500" />
              </div>
            ) : expandedDetails ? (
              <ExpandedOperationDetail 
                operation={expandedDetails}
                onCancel={onCancel}
                onRetry={onRetry}
                onViewCorrelated={onViewCorrelated}
              />
            ) : (
              <div className="p-8 text-center text-surface-500">
                Failed to load operation details
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// Expanded Operation Detail Component
// ============================================

interface ExpandedDetailProps {
  operation: OperationWithSteps;
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
  onViewCorrelated: (correlationId: string) => void;
}

function ExpandedOperationDetail({ operation, onCancel, onRetry, onViewCorrelated }: ExpandedDetailProps) {
  const [activeTab, setActiveTab] = useState<'steps' | 'events' | 'resources'>('steps');
  
  const isActive = ['running', 'queued', 'blocked'].includes(operation.status);
  const canRetry = operation.isRetryable && ['failed', 'partial'].includes(operation.status);

  // Fetch events for this operation
  const { data: events = [] } = useQuery({
    queryKey: ['operation-events', operation.id],
    queryFn: () => operationsApi.getEvents(operation.id, undefined, 100),
    enabled: activeTab === 'events',
  });

  return (
    <div className="p-6 space-y-6">
      {/* Operation Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white mb-1">{operation.title}</h3>
          {operation.description && (
            <p className="text-sm text-surface-400 mb-2">{operation.description}</p>
          )}
          <div className="flex items-center gap-4 text-xs text-surface-500 flex-wrap">
            <IdWithCopy id={operation.id} label="ID" truncate={16} />
            <button
              onClick={() => onViewCorrelated(operation.correlationId)}
              className="flex items-center gap-1 hover:text-blue-400 transition"
            >
              <Link className="w-3 h-3" />
              Correlation: {operation.correlationId.slice(0, 8)}...
            </button>
            <span>
              Started: {format(new Date(operation.createdAt), 'MMM d, HH:mm:ss')}
            </span>
            {operation.durationMs && (
              <span>Duration: {formatDuration(operation.durationMs)}</span>
            )}
          </div>
          
          {/* External Links */}
          <div className="mt-3">
            <ExternalLinksPanel operation={operation} />
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isActive && (
            <button
              onClick={() => {
                if (confirm('Cancel this operation?')) {
                  onCancel(operation.id);
                }
              }}
              className="px-3 py-1.5 text-xs font-medium text-red-400 hover:bg-red-500/10 
                         rounded-lg transition border border-red-500/20"
            >
              Cancel
            </button>
          )}
          {canRetry && (
            <button
              onClick={() => onRetry(operation.id)}
              className="px-3 py-1.5 text-xs font-medium text-blue-400 hover:bg-blue-500/10 
                         rounded-lg transition border border-blue-500/20 flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" />
              Retry
            </button>
          )}
        </div>
      </div>

      {/* Blocking Factors */}
      {operation.blockingFactors && operation.blockingFactors.length > 0 && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <h4 className="text-sm font-medium text-amber-400 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Blocking Factors
          </h4>
          <div className="space-y-3">
            {operation.blockingFactors.map((factor, i) => (
              <div key={i} className="p-3 bg-amber-500/10 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-amber-300 capitalize">
                      {factor.type.replace(/_/g, ' ')}
                    </div>
                    <p className="text-xs text-amber-300/80 mt-1">{factor.description}</p>
                    {factor.suggestedAction && (
                      <div className="mt-2 p-2 bg-amber-500/10 rounded text-xs text-amber-200">
                        <span className="font-medium">💡 Suggested Action:</span> {factor.suggestedAction}
                      </div>
                    )}
                    <div className="text-[10px] text-amber-400/60 mt-2">
                      Since: {formatDistanceToNow(new Date(factor.since), { addSuffix: true })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* State Mismatches */}
      {operation.stateMismatches && operation.stateMismatches.length > 0 && (
        <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
          <h4 className="text-sm font-medium text-orange-400 mb-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            State Mismatches (Control Plane ≠ Data Plane)
          </h4>
          <div className="space-y-2">
            {operation.stateMismatches.map((mismatch, i) => (
              <div key={i} className="flex items-center gap-4 p-2 bg-orange-500/10 rounded">
                <div className="text-xs">
                  <span className="text-orange-400 capitalize">{mismatch.resourceType}</span>
                  <code className="ml-2 text-orange-300">{mismatch.resourceId}</code>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-surface-500">Expected:</span>
                  <code className="px-1 bg-emerald-500/20 text-emerald-400 rounded">{mismatch.expectedState}</code>
                  <span className="text-surface-600">→</span>
                  <span className="text-surface-500">Actual:</span>
                  <code className="px-1 bg-red-500/20 text-red-400 rounded">{mismatch.actualState}</code>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Message */}
      {operation.errorMessage && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <h4 className="text-sm font-medium text-red-400 mb-1 flex items-center gap-2">
            <XCircle className="w-4 h-4" />
            Error
          </h4>
          <p className="text-sm text-red-300">{operation.errorMessage}</p>
          {operation.errorContext && (
            <pre className="mt-2 p-2 bg-red-500/10 rounded text-xs text-red-300/70 overflow-x-auto">
              {JSON.stringify(operation.errorContext, null, 2)}
            </pre>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-surface-700">
        <div className="flex gap-4">
          {[
            { id: 'steps', label: 'Execution Steps', icon: ListTree, count: operation.stepStats?.total },
            { id: 'events', label: 'Events', icon: History },
            { id: 'resources', label: 'Resources', icon: Server },
          ].map(({ id, label, icon: Icon, count }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id as typeof activeTab)}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition -mb-px',
                activeTab === id
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-surface-400 hover:text-surface-300'
              )}
            >
              <Icon className="w-4 h-4" />
              {label}
              {count !== undefined && (
                <span className="px-1.5 py-0.5 text-xs bg-surface-800 rounded">{count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'steps' && (
          <motion.div
            key="steps"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {operation.steps && operation.steps.length > 0 ? (
              <OperationStepsTimeline 
                steps={operation.steps} 
                stepStats={operation.stepStats}
              />
            ) : (
              <div className="text-center py-8 text-surface-500">
                No steps recorded for this operation
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'events' && (
          <motion.div
            key="events"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <EventsTimeline events={events} />
          </motion.div>
        )}

        {activeTab === 'resources' && (
          <motion.div
            key="resources"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ResourcesPanel resources={operation.targetResources} region={operation.targetResources?.region} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// Events Timeline Component
// ============================================

function EventsTimeline({ events }: { events: OperationEvent[] }) {
  const [severityFilter, setSeverityFilter] = useState<EventSeverity | 'all'>('all');
  
  const filteredEvents = useMemo(() => {
    if (severityFilter === 'all') return events;
    return events.filter(e => e.severity === severityFilter);
  }, [events, severityFilter]);

  if (events.length === 0) {
    return (
      <div className="text-center py-8 text-surface-500">
        <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No events recorded for this operation</p>
      </div>
    );
  }

  return (
    <div>
      {/* Severity Filter */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-surface-400">Filter:</span>
        <div className="flex gap-1">
          {(['all', 'critical', 'error', 'warning', 'info', 'debug'] as const).map((sev) => (
            <button
              key={sev}
              onClick={() => setSeverityFilter(sev)}
              className={clsx(
                'px-2 py-1 text-xs rounded transition capitalize',
                severityFilter === sev
                  ? sev === 'all' 
                    ? 'bg-blue-500 text-white'
                    : `${EVENT_SEVERITY_CONFIG[sev as EventSeverity].bgColor} ${EVENT_SEVERITY_CONFIG[sev as EventSeverity].color}`
                  : 'bg-surface-800 text-surface-400 hover:bg-surface-700'
              )}
            >
              {sev}
            </button>
          ))}
        </div>
      </div>

      {/* Events List */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {filteredEvents.map((event) => {
          const config = EVENT_SEVERITY_CONFIG[event.severity];
          const Icon = config.icon;
          
          return (
            <div 
              key={event.id}
              className={clsx(
                'p-3 rounded-lg border',
                config.bgColor,
                event.severity === 'critical' ? 'border-red-500/30' :
                event.severity === 'error' ? 'border-red-500/20' :
                event.severity === 'warning' ? 'border-amber-500/20' :
                'border-surface-700'
              )}
            >
              <div className="flex items-start gap-3">
                <Icon className={clsx('w-4 h-4 flex-shrink-0 mt-0.5', config.color)} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={clsx('text-xs font-medium', config.color)}>
                      {config.label}
                    </span>
                    <span className="text-xs text-surface-500">
                      {format(new Date(event.timestamp), 'HH:mm:ss')}
                    </span>
                    <span className="text-xs text-surface-500 capitalize">
                      via {event.source}
                    </span>
                  </div>
                  <p className="text-sm text-surface-300">{event.message}</p>
                  {event.details && (
                    <p className="text-xs text-surface-400 mt-1">{event.details}</p>
                  )}
                  {event.resourceId && (
                    <div className="text-xs text-surface-500 mt-1">
                      Resource: <code className="text-surface-400">{event.resourceType}: {event.resourceId}</code>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// Resources Panel Component
// ============================================

function ResourcesPanel({ resources, region = 'us-east-1' }: { resources: Operation['targetResources']; region?: string }) {
  if (!resources || Object.keys(resources).length === 0) {
    return (
      <div className="text-center py-8 text-surface-500">
        No resources associated with this operation
      </div>
    );
  }

  type ResourceItem = { label: string; value: string | undefined; link?: string };
  
  const sections: Array<{
    title: string;
    icon: React.ComponentType<any>;
    items: ResourceItem[];
  }> = [
    {
      title: 'Compute Resources',
      icon: Server,
      items: ([
        { label: 'Instance ID', value: resources.instanceId, link: resources.instanceId ? getAWSConsoleLink('instance', resources.instanceId, region) : undefined },
        { label: 'Instance Type', value: resources.instanceType },
        { label: 'Container ID', value: resources.containerId },
        { label: 'Pod Name', value: resources.podName },
        { label: 'Task ARN', value: resources.taskArn },
        { label: 'Node', value: resources.nodeName },
      ] as ResourceItem[]).filter(i => i.value),
    },
    {
      title: 'GPU Resources',
      icon: Cpu,
      items: ([
        { label: 'GPU Type', value: resources.gpuType },
        { label: 'GPU Count', value: resources.gpuCount?.toString() },
        { label: 'GPU Device IDs', value: resources.gpuDeviceIds?.join(', ') },
        { label: 'GPU Memory', value: resources.gpuMemoryMb ? `${(resources.gpuMemoryMb / 1024).toFixed(1)} GB` : undefined },
        { label: 'CUDA Version', value: resources.cudaVersion },
      ] as ResourceItem[]).filter(i => i.value),
    },
    {
      title: 'Network Resources',
      icon: Network,
      items: ([
        { label: 'VPC ID', value: resources.vpcId, link: resources.vpcId ? getAWSConsoleLink('vpc', resources.vpcId, region) : undefined },
        { label: 'Subnet ID', value: resources.subnetId, link: resources.subnetId ? getAWSConsoleLink('subnet', resources.subnetId, region) : undefined },
        { label: 'Security Group', value: resources.securityGroupId, link: resources.securityGroupId ? getAWSConsoleLink('security_group', resources.securityGroupId, region) : undefined },
        { label: 'Region', value: resources.region },
        { label: 'Availability Zone', value: resources.availabilityZone },
      ] as ResourceItem[]).filter(i => i.value),
    },
    {
      title: 'Container Config',
      icon: Box,
      items: ([
        { label: 'Image', value: resources.containerImage },
        { label: 'Namespace', value: resources.namespace },
        { label: 'Deployment', value: resources.deploymentName },
        { label: 'Service', value: resources.serviceName },
      ] as ResourceItem[]).filter(i => i.value),
    },
  ].filter(section => section.items.length > 0);

  return (
    <div className="grid grid-cols-2 gap-4">
      {sections.map((section) => {
        const Icon = section.icon;
        return (
          <div key={section.title} className="p-4 bg-surface-800/50 rounded-lg">
            <h4 className="text-xs font-medium text-surface-400 mb-3 flex items-center gap-2">
              <Icon className="w-3.5 h-3.5" />
              {section.title}
            </h4>
            <div className="space-y-2">
              {section.items.map((item) => (
                <div key={item.label} className="flex justify-between text-sm">
                  <span className="text-surface-500">{item.label}</span>
                  {item.link ? (
                    <a 
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 truncate max-w-[200px] flex items-center gap-1"
                      title={item.value}
                    >
                      {item.value}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span className="text-surface-300 truncate max-w-[200px]" title={item.value}>
                      {item.value}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// Main Operations Hub Page
// ============================================

export default function OperationsHubPage() {
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    status: [],
    type: [],
    triggerSource: [],
    category: 'all',
    timeRange: '24h',
    hasIssues: false,
    search: '',
  });

  // Build API filters from state
  const apiFilters: OperationFilters = useMemo(() => {
    const categoryTypes = OPERATION_CATEGORIES[filters.category];
    const typeFilter = filters.type.length > 0 
      ? filters.type 
      : (categoryTypes.length > 0 ? categoryTypes : undefined);

    return {
      status: filters.status.length > 0 ? filters.status : undefined,
      type: typeFilter,
      triggerSource: filters.triggerSource.length === 1 ? filters.triggerSource[0] : undefined,
      hasIssues: filters.hasIssues || undefined,
      limit: 100,
    };
  }, [filters]);

  // Fetch operations
  const { data: operationsData, isLoading, isFetching } = useQuery({
    queryKey: ['operations-hub', apiFilters],
    queryFn: () => operationsApi.list(apiFilters),
    refetchInterval: (query) => {
      const hasActive = query.state.data?.data?.some(op =>
        ['queued', 'running', 'blocked'].includes(op.status)
      );
      return hasActive ? 3000 : 15000;
    },
  });

  // Fetch attention items
  const { data: attention } = useQuery({
    queryKey: ['operations-attention'],
    queryFn: operationsApi.getAttention,
    refetchInterval: 10000,
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['operations-stats', filters.timeRange],
    queryFn: () => operationsApi.getStats(filters.timeRange as '1h' | '24h' | '7d' | '30d'),
    refetchInterval: 30000,
  });

  // Fetch expanded operation details with steps
  const { data: expandedDetails, isLoading: isLoadingDetails } = useQuery({
    queryKey: ['operation-detail-steps', expandedId],
    queryFn: () => expandedId ? operationsApi.getWithSteps(expandedId) : null,
    enabled: !!expandedId,
    refetchInterval: expandedId ? 2000 : false,
  });

  // Mutations
  const cancelMutation = useMutation({
    mutationFn: (id: string) => operationsApi.cancel(id),
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Operation cancelled');
        queryClient.invalidateQueries({ queryKey: ['operations-hub'] });
      } else {
        toast.error(result.message || 'Failed to cancel');
      }
    },
    onError: () => toast.error('Failed to cancel operation'),
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => operationsApi.retry(id),
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Retry initiated');
        queryClient.invalidateQueries({ queryKey: ['operations-hub'] });
      } else {
        toast.error(result.message || 'Failed to retry');
      }
    },
    onError: () => toast.error('Failed to retry operation'),
  });

  // Filter operations by search
  const filteredOperations = useMemo(() => {
    const operations = operationsData?.data || [];
    if (!filters.search) return operations;
    
    const q = filters.search.toLowerCase();
    return operations.filter(op =>
      op.id.toLowerCase().includes(q) ||
      op.correlationId.toLowerCase().includes(q) ||
      op.targetResources?.instanceId?.toLowerCase().includes(q) ||
      op.targetResources?.containerId?.toLowerCase().includes(q) ||
      op.targetResources?.podName?.toLowerCase().includes(q) ||
      op.title.toLowerCase().includes(q)
    );
  }, [operationsData, filters.search]);

  // Handlers
  const handleViewCorrelated = useCallback((correlationId: string) => {
    setFilters(prev => ({ ...prev, search: correlationId }));
    toast('Showing correlated operations', { icon: '🔗' });
  }, []);

  const handleFilterIssues = useCallback(() => {
    setFilters(prev => ({ ...prev, hasIssues: true }));
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="container mx-auto max-w-[1800px]"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="p-2.5 sm:p-3 bg-gradient-to-br from-emerald-500 via-blue-500 to-purple-500 rounded-xl shadow-lg flex-shrink-0">
            <Activity className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-white">Operations Hub</h1>
            <p className="text-surface-400 text-xs sm:text-sm">
              Single source of truth for GPU container operational movements
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Live indicator */}
          {(operationsData?.data?.some(op => ['running', 'queued'].includes(op.status))) && (
            <span className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 
                             rounded-full text-xs font-medium text-emerald-400">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Live
            </span>
          )}
          
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['operations-hub'] })}
            disabled={isFetching}
            className="flex items-center gap-2 px-4 py-2 bg-surface-800 hover:bg-surface-700 
                       rounded-lg transition text-sm font-medium border border-surface-700"
          >
            <RefreshCw className={clsx('w-4 h-4', isFetching && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </div>

      {/* Operational Intelligence */}
      <OperationalIntelligencePanel 
        attention={attention || null}
        stats={stats || null}
        onFilterIssues={handleFilterIssues}
      />

      {/* Filters */}
      <FilterBar
        filters={filters}
        onFiltersChange={setFilters}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onExport={() => setShowExportDialog(true)}
        operationsCount={filteredOperations.length}
      />

      {/* Operations List */}
      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-surface-500" />
        </div>
      ) : filteredOperations.length === 0 ? (
        <div className="text-center py-20">
          <Activity className="w-16 h-16 mx-auto mb-4 text-surface-700" />
          <p className="text-surface-400 text-lg">No operations found</p>
          <p className="text-surface-500 text-sm mt-1">
            {filters.search || filters.status.length > 0 || filters.hasIssues
              ? 'Try adjusting your filters'
              : 'Operations will appear here when GPU resources are created or modified'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence mode="popLayout">
            {filteredOperations.map((operation) => (
              <OperationRow
                key={operation.id}
                operation={operation}
                isExpanded={expandedId === operation.id}
                onToggle={() => setExpandedId(expandedId === operation.id ? null : operation.id)}
                expandedDetails={expandedId === operation.id ? expandedDetails : null}
                isLoadingDetails={expandedId === operation.id && isLoadingDetails}
                onCancel={(id) => cancelMutation.mutate(id)}
                onRetry={(id) => retryMutation.mutate(id)}
                onViewCorrelated={handleViewCorrelated}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Pagination Info */}
      {operationsData && operationsData.total > 0 && (
        <div className="mt-6 flex items-center justify-between text-sm text-surface-500">
          <span>
            Showing {filteredOperations.length} of {operationsData.total} operations
          </span>
          {operationsData.total > 100 && (
            <button className="text-blue-400 hover:text-blue-300 transition">
              Load more...
            </button>
          )}
        </div>
      )}

      {/* Export Dialog */}
      <ExportDialog
        operations={filteredOperations}
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
      />
    </motion.div>
  );
}
